import React from 'react';
import { ADVANTAGES, ADVANTAGES_DEFAULTS, isAdvantageOn, type AdvantagesConfig } from '../checkout-components/CheckoutAdvantages';

interface AdvantagesInspectorProps {
  config: AdvantagesConfig;
  onConfigChange: (patch: Partial<AdvantagesConfig>) => void;
}

const inputCls =
  'w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500';
const labelCls = 'block text-sm text-gray-700 dark:text-gray-300 mb-1';
const divider = <hr className="border-0 border-t border-dashed border-gray-300 dark:border-gray-700" />;

const Toggle: React.FC<{ checked: boolean; onChange: (v: boolean) => void; label: string }> = ({ checked, onChange, label }) => (
  <label className="flex items-center gap-3 cursor-pointer select-none">
    <span className={`relative inline-block w-10 h-5 rounded-full transition-colors ${checked ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'}`}>
      <input type="checkbox" className="sr-only" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span className={`absolute top-0.5 w-4 h-4 rounded-full shadow transition-all ${checked ? 'left-[22px] bg-white' : 'left-0.5 bg-gray-500'}`} />
    </span>
    <span className="text-base text-gray-800 dark:text-gray-200">{label}</span>
  </label>
);

const ColorDot: React.FC<{ value: string; onChange: (v: string) => void; title: string }> = ({ value, onChange, title }) => (
  <label
    className="relative inline-block w-7 h-7 rounded-full border-2 border-gray-300 cursor-pointer overflow-hidden"
    style={{ backgroundColor: value }}
    title={title}
  >
    <input type="color" className="absolute inset-0 opacity-0 cursor-pointer" value={value} onChange={(e) => onChange(e.target.value)} />
  </label>
);

const AdvantagesInspector: React.FC<AdvantagesInspectorProps> = ({ config, onConfigChange }) => {
  const dark = config.theme === 'dark';
  const bgNone = config.backgroundColor === 'none';
  const bgCustom = !!config.backgroundColor && !bgNone;
  return (
    <div className="space-y-4">
      <div>
        <label className={labelCls}>Tema de cores</label>
        <select className={inputCls} value={config.theme || ADVANTAGES_DEFAULTS.theme} onChange={(e) => onConfigChange({ theme: e.target.value as 'light' | 'dark' })}>
          <option value="light">Light</option>
          <option value="dark">Dark</option>
        </select>
      </div>

      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-700 dark:text-gray-300">
          Cor de fundo
          <span className="block text-[11px] text-gray-400">{bgNone ? 'Sem fundo' : bgCustom ? 'Cor personalizada' : 'Padrão do tema'}</span>
        </span>
        <div className="flex items-center gap-2">
          {(bgCustom || bgNone) && (
            <button type="button" className="text-xs text-gray-500 hover:underline" onClick={() => onConfigChange({ backgroundColor: undefined })}>
              Padrão
            </button>
          )}
          {!bgNone && (
            <button type="button" className="text-xs text-gray-500 hover:underline" onClick={() => onConfigChange({ backgroundColor: 'none' })}>
              Sem fundo
            </button>
          )}
          <ColorDot title="Cor de fundo" value={bgCustom ? (config.backgroundColor as string) : dark ? '#111827' : '#ffffff'} onChange={(v) => onConfigChange({ backgroundColor: v })} />
        </div>
      </div>

      <p className="text-[11px] text-gray-400 leading-snug">
        A orientação é automática: na coluna lateral e no celular as vantagens ficam uma embaixo da outra; na caixa de cima ficam lado a lado.
      </p>

      {divider}

      <div className="space-y-4">
        {ADVANTAGES.map((a) => (
          <Toggle
            key={a.key}
            label={a.title}
            checked={isAdvantageOn(config, a.key)}
            onChange={(v) => onConfigChange({ [a.key]: v } as Partial<AdvantagesConfig>)}
          />
        ))}
      </div>

      {divider}

      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-700 dark:text-gray-300">Cor Principal</span>
        <ColorDot title="Cor principal" value={config.mainColor || ADVANTAGES_DEFAULTS.mainColor} onChange={(v) => onConfigChange({ mainColor: v })} />
      </div>
      <div className="flex items-center justify-between">
        <span className={`text-sm ${dark ? 'text-gray-400' : 'text-gray-700 dark:text-gray-300'}`}>
          Cor do Título{dark && <span className="block text-[11px]">No tema Dark o título fica branco</span>}
        </span>
        <ColorDot title="Cor do título" value={config.titleColor || ADVANTAGES_DEFAULTS.titleColor} onChange={(v) => onConfigChange({ titleColor: v })} />
      </div>
    </div>
  );
};

export default AdvantagesInspector;
